import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft, Image as ImageIcon, Loader2, CheckCircle2, AlertCircle,
  ExternalLink, Upload, Wand2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/**
 * AdminAvatarTool — point-and-click utility for setting any creator's
 * storefront avatar without touching the terminal.
 *
 * Three lookup modes:
 *   • Email   → /api/admin/creators/set-avatar { email, avatar_url }
 *   • Slug    → ...                            { store_slug, avatar_url }
 *   • CrId    → ...                            { creator_id, avatar_url }
 *
 * Two avatar source flows:
 *   1. Paste an absolute URL (works for any externally-hosted logo)
 *   2. Upload a file → goes to /api/upload/image (GridFS-backed) → returns
 *      the persistent /api/images/{id} URL, then we apply it.
 *
 * Why this page exists: creator profile avatars uploaded via the dashboard
 * occasionally don't take effect (the user can't tell if it's their cache,
 * their internet, or a bug). This page lets the admin force-apply the
 * right asset in 5 seconds and verify the result inline.
 */
export default function AdminAvatarTool() {
  const [lookupMode, setLookupMode] = useState('email'); // email | slug | creator_id
  const [lookupValue, setLookupValue] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [applying, setApplying] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [lastError, setLastError] = useState('');

  const PRESETS = [
    { label: 'WhyIQ (bundled)', value: 'https://theailedger.ai/seed-avatars/whyiq.png' },
  ];

  const applyAvatar = async () => {
    if (!lookupValue.trim()) {
      toast.error('Enter the lookup value first');
      return;
    }
    if (!avatarUrl.trim()) {
      toast.error('Provide an avatar URL (or upload one)');
      return;
    }
    setApplying(true);
    setLastError('');
    try {
      const payload = { avatar_url: avatarUrl.trim() };
      payload[lookupMode] = lookupValue.trim();
      const res = await axios.post(
        `${API}/api/admin/creators/set-avatar`,
        payload,
        { headers: auth() },
      );
      setLastResult(res.data);
      toast.success('Avatar applied');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed';
      setLastError(msg);
      toast.error(msg);
    } finally {
      setApplying(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5MB or smaller');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/upload/image`, fd, {
        headers: { ...auth() },
      });
      const url = res.data?.url;
      if (!url) throw new Error('Upload returned no URL');
      // Resolve to absolute URL so it's usable across both Preview and Prod
      // domains without ambiguity.
      const absoluteUrl = url.startsWith('http') ? url : `${API}${url}`;
      setAvatarUrl(absoluteUrl);
      toast.success('Uploaded — now click Apply Avatar');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-avatar-tool">
      <header className="border-b border-white/10 sticky top-0 bg-black/95 backdrop-blur z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-neutral-400 hover:text-white inline-flex items-center gap-1 text-sm">
              <ChevronLeft className="w-4 h-4" /> Admin
            </Link>
            <span className="text-neutral-700">|</span>
            <h1 className="font-bold text-lg inline-flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-[#00F0FF]" />
              Avatar Tool
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
          <h2 className="font-bold text-base mb-1">Force-set a creator's storefront avatar</h2>
          <p className="text-xs text-neutral-400 leading-relaxed">
            Use this when a creator can't get their avatar to take effect from their dashboard.
            Look them up by email, store slug, or creator_id; provide an absolute URL OR upload
            a logo and we'll host it on GridFS for you. Updates both <code className="text-[#00F0FF]">users.avatar</code> and <code className="text-[#00F0FF]">creators.avatar</code>.
          </p>
        </div>

        {/* Lookup mode toggle */}
        <div className="rounded-xl border border-white/10 bg-neutral-950 p-5 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-400 mb-2 block">
              1. Find the creator
            </label>
            <div className="flex gap-1 mb-3 p-1 rounded-lg bg-white/[0.04] border border-white/10 w-fit">
              {[
                { v: 'email', l: 'Email' },
                { v: 'store_slug', l: 'Store slug' },
                { v: 'creator_id', l: 'Creator ID' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  data-testid={`lookup-mode-${opt.v}`}
                  onClick={() => { setLookupMode(opt.v); setLookupValue(''); }}
                  className={`px-3 py-1 text-xs rounded font-semibold transition-colors ${
                    lookupMode === opt.v
                      ? 'bg-[#00F0FF] text-black'
                      : 'text-neutral-300 hover:text-white'
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={lookupValue}
              onChange={(e) => setLookupValue(e.target.value)}
              placeholder={
                lookupMode === 'email' ? 'creator@example.com' :
                lookupMode === 'store_slug' ? 'creators-store-slug' :
                'creator_xxxxxxxxxx'
              }
              data-testid="lookup-value-input"
              className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-neutral-500 focus:border-[#00F0FF]/50 focus:outline-none font-mono"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-neutral-400 mb-2 block">
              2. Avatar source
            </label>
            <input
              type="text"
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
              placeholder="https://example.com/logo.png  (or upload below)"
              data-testid="avatar-url-input"
              className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-neutral-500 focus:border-[#00F0FF]/50 focus:outline-none font-mono mb-2"
            />

            <div className="flex flex-wrap items-center gap-2">
              <label
                htmlFor="avatar-upload-input"
                data-testid="upload-trigger"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-white/15 bg-white/[0.03] hover:bg-white/[0.06] cursor-pointer font-semibold"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Uploading…
                  </>
                ) : (
                  <>
                    <Upload className="w-3.5 h-3.5" />
                    Upload image
                  </>
                )}
                <input
                  id="avatar-upload-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  className="hidden"
                  data-testid="avatar-upload-input"
                />
              </label>

              <span className="text-[11px] text-neutral-500">or one-click preset:</span>
              {PRESETS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setAvatarUrl(p.value)}
                  data-testid={`preset-${p.label}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-full border border-[#00F0FF]/25 bg-[#00F0FF]/[0.04] text-[#00F0FF] hover:bg-[#00F0FF]/[0.08] font-semibold"
                >
                  <Wand2 className="w-3 h-3" />
                  {p.label}
                </button>
              ))}
            </div>

            {avatarUrl && (
              <div className="mt-3 flex items-center gap-3 p-2 rounded-lg bg-black/40 border border-white/10">
                <img
                  src={avatarUrl}
                  alt="preview"
                  data-testid="avatar-preview-img"
                  className="w-14 h-14 rounded-lg object-cover border border-white/10 bg-neutral-900"
                  onError={(e) => { e.target.style.opacity = 0.3; }}
                />
                <div className="text-xs text-neutral-400 truncate font-mono flex-1">{avatarUrl}</div>
              </div>
            )}
          </div>

          <button
            onClick={applyAvatar}
            disabled={applying || !lookupValue || !avatarUrl}
            data-testid="apply-avatar-btn"
            className="w-full inline-flex items-center justify-center gap-1.5 bg-[#00F0FF] text-black font-bold py-2.5 rounded-lg hover:bg-[#00F0FF]/90 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {applying ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Applying…
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Apply avatar
              </>
            )}
          </button>
        </div>

        {/* Result panel */}
        {lastResult && (
          <div
            className="rounded-xl border border-emerald-400/30 bg-emerald-400/[0.04] p-5"
            data-testid="apply-result"
          >
            <div className="flex items-center gap-2 mb-3 text-emerald-300 font-bold text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Applied successfully
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <Field label="Creator ID" value={lastResult.creator_id} mono />
              <Field label="User ID" value={lastResult.user_id} mono />
              <Field label="Store slug" value={lastResult.store_slug} mono />
              <Field label="Modified rows" value={`creators: ${lastResult.creators_modified} · users: ${lastResult.users_modified}`} />
            </div>
            <div className="mt-3 flex items-center gap-2">
              <img
                src={lastResult.avatar}
                alt="applied"
                className="w-12 h-12 rounded-lg object-cover border border-emerald-400/30"
              />
              <a
                href={lastResult.avatar}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-emerald-300 inline-flex items-center gap-1 underline"
              >
                Open URL <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}

        {lastError && (
          <div
            className="rounded-xl border border-red-400/30 bg-red-400/[0.04] p-5"
            data-testid="apply-error"
          >
            <div className="flex items-center gap-2 mb-1 text-red-300 font-bold text-sm">
              <AlertCircle className="w-4 h-4" />
              Could not apply
            </div>
            <div className="text-xs text-red-200/80">{lastError}</div>
          </div>
        )}
      </main>
    </div>
  );
}

const Field = ({ label, value, mono }) => (
  <div className="rounded-lg bg-black/30 border border-white/10 p-2">
    <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-0.5">{label}</div>
    <div className={`text-white text-xs truncate ${mono ? 'font-mono' : ''}`}>{value || '—'}</div>
  </div>
);
